// Subscriptions and discount codes for business ops.
import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Card, Grid, Table, TableHead, TableBody, TableRow, TableCell,
    TableContainer, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Select, MenuItem, FormControl, InputLabel, Alert, CircularProgress,
    Tabs, Tab
} from '@mui/material';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebase';

const Subscriptions = () => {
    const [tab, setTab] = useState(0);
    const [users, setUsers] = useState([]);
    const [codes, setCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState({
        code: '',
        discountType: 'percentage',
        discountValue: 10,
        description: '',
        maxUses: 100,
    });

    const load = async () => {
        setLoading(true);
        try {
            const [usersSnap, codesSnap] = await Promise.all([
                getDocs(collection(db, 'users')),
                getDocs(collection(db, 'discount_codes')),
            ]);
            setUsers(usersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
            setCodes(codesSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        } catch (e) {
            console.error(e);
            setAlert({ type: 'error', text: 'Failed to load subscription data' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const paidStatus = (u) => {
        const nested = String(u.subscription?.tier || '').replace('SubscriptionTier.', '');
        const status = u.subscriptionStatus || nested;
        return ['premium', 'pro', 'family'].includes(status);
    };
    const paid = users.filter(paidStatus);
    const free = users.filter((u) => !paidStatus(u));

    const saveCode = async () => {
        if (!form.code.trim()) {
            setAlert({ type: 'error', text: 'Code is required' });
            return;
        }
        try {
            await addDoc(collection(db, 'discount_codes'), {
                code: form.code.trim().toUpperCase(),
                discountType: form.discountType,
                discountValue: Number(form.discountValue) || 0,
                description: form.description.trim(),
                maxUses: Number(form.maxUses) || 0,
                currentUses: 0,
                isActive: true,
                createdAt: Timestamp.now(),
                applicablePlans: ['premium', 'family'],
            });
            setDialogOpen(false);
            setForm({ code: '', discountType: 'percentage', discountValue: 10, description: '', maxUses: 100 });
            setAlert({ type: 'success', text: 'Discount code created' });
            load();
        } catch (e) {
            console.error(e);
            setAlert({ type: 'error', text: 'Failed to create code' });
        }
    };

    const toggleCode = async (code) => {
        try {
            await updateDoc(doc(db, 'discount_codes', code.id), { isActive: !code.isActive });
            load();
        } catch (e) {
            setAlert({ type: 'error', text: 'Failed to update code' });
        }
    };

    const removeCode = async (code) => {
        if (!window.confirm(`Delete ${code.code}?`)) return;
        try {
            await deleteDoc(doc(db, 'discount_codes', code.id));
            load();
        } catch (e) {
            setAlert({ type: 'error', text: 'Failed to delete code' });
        }
    };

    return (
        <Box>
            <Typography variant="h4" sx={{ mb: 3 }}>Subscriptions</Typography>
            {alert && (
                <Alert severity={alert.type} onClose={() => setAlert(null)} sx={{ mb: 2 }}>
                    {alert.text}
                </Alert>
            )}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Card sx={{ p: 2 }}>
                        <Typography color="text.secondary">Paid accounts</Typography>
                        <Typography variant="h4">{paid.length}</Typography>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Card sx={{ p: 2 }}>
                        <Typography color="text.secondary">Free accounts</Typography>
                        <Typography variant="h4">{free.length}</Typography>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Card sx={{ p: 2 }}>
                        <Typography color="text.secondary">Active discount codes</Typography>
                        <Typography variant="h4">{codes.filter((c) => c.isActive).length}</Typography>
                    </Card>
                </Grid>
            </Grid>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                <Tab label="Paid users" />
                <Tab label="Discount codes" />
            </Tabs>

            {loading ? (
                <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
            ) : tab === 0 ? (
                <Card>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Plan</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Platform</TableCell>
                                    <TableCell>Expires</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paid.map((user) => {
                                    const expiry = user.subscriptionExpiryDate;
                                    const expiryDate = expiry?.toDate ? expiry.toDate() : expiry ? new Date(expiry) : null;
                                    const isExpired = expiryDate && expiryDate < new Date();
                                    return (
                                    <TableRow key={user.id}>
                                        <TableCell>{user.email || user.id}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={user.subscriptionStatus} color="success" />
                                        </TableCell>
                                        <TableCell>{user.displayName || '—'}</TableCell>
                                        <TableCell>{user.subscriptionPlatform || '—'}</TableCell>
                                        <TableCell>
                                            {expiryDate ? (
                                                <Chip
                                                    size="small"
                                                    label={expiryDate.toLocaleDateString()}
                                                    color={isExpired ? 'error' : 'default'}
                                                    variant="outlined"
                                                />
                                            ) : '—'}
                                        </TableCell>
                                    </TableRow>
                                    );
                                })}
                                {paid.length === 0 && (
                                    <TableRow><TableCell colSpan={5}>No paid subscriptions yet.</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            ) : (
                <Card>
                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button variant="contained" onClick={() => setDialogOpen(true)}>New code</Button>
                    </Box>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Code</TableCell>
                                    <TableCell>Value</TableCell>
                                    <TableCell>Uses</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {codes.map((code) => (
                                    <TableRow key={code.id}>
                                        <TableCell>{code.code}</TableCell>
                                        <TableCell>
                                            {code.discountType === 'percentage' ? `${code.discountValue}%` : `$${code.discountValue}`}
                                        </TableCell>
                                        <TableCell>{code.currentUses || 0} / {code.maxUses || '∞'}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={code.isActive ? 'Active' : 'Paused'} color={code.isActive ? 'success' : 'default'} />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button size="small" onClick={() => toggleCode(code)}>
                                                {code.isActive ? 'Pause' : 'Activate'}
                                            </Button>
                                            <Button size="small" color="error" onClick={() => removeCode(code)}>Delete</Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                <DialogTitle>New discount code</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2, minWidth: 360 }}>
                    <TextField label="Code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
                    <FormControl>
                        <InputLabel>Type</InputLabel>
                        <Select label="Type" value={form.discountType} onChange={(e) => setForm({ ...form, discountType: e.target.value })}>
                            <MenuItem value="percentage">Percentage</MenuItem>
                            <MenuItem value="fixed">Fixed amount</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField type="number" label="Value" value={form.discountValue} onChange={(e) => setForm({ ...form, discountValue: e.target.value })} />
                    <TextField type="number" label="Max uses" value={form.maxUses} onChange={(e) => setForm({ ...form, maxUses: e.target.value })} />
                    <TextField label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={saveCode}>Create</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Subscriptions;
